export const PROPERTY_TYPE = "properties";
export const FOLDER_TYPE = "folders";

export type FiscaliteItemType = typeof PROPERTY_TYPE | typeof FOLDER_TYPE;

export interface FiscaliteItem {
  id: number;
  nom?: string;
  rue?: string | null;
  cp?: string | null;
  ville?: string | null;
  date_achat?: string | null;
  prix_achat?: number | null;
  compta_fiscal_years?: number[];
  is_sent_to_compta?: boolean;
  is_transac_sent_to_compta?: Record<number, boolean>;
  compta_payment_in_process?: number[];
  id_profile_nom_owner?: number;
}

export interface FiscaliteProfile {
  id: number;
  nom_profile: string;
  is_sent_to_compta?: boolean;
  regime_fiscal?: string;
  siret?: string | null;
  debut_activite?: string | null;
}

export interface Declaration {
  profileId: number;
  profileName: string;
  year: number;
  properties: FiscaliteItem[];
  folders: FiscaliteItem[];
  profile: FiscaliteProfile;
  profileSent: boolean;
  propertiesSent: boolean;
  transacSent: boolean;
  tasksDone: number;
  tasksTotal: number;
}

interface BuildDeclarationsArgs {
  properties: FiscaliteItem[];
  propertyFolders: FiscaliteItem[];
  profileBailleursById: Record<number, FiscaliteProfile>;
}

export function buildDeclarations({
  properties,
  propertyFolders,
  profileBailleursById,
}: BuildDeclarationsArgs): Declaration[] {
  const allYears = Array.from(
    new Set([
      ...properties.flatMap((p) => p.compta_fiscal_years || []),
      ...propertyFolders.flatMap((f) => f.compta_fiscal_years || []),
    ]),
  );

  const declarations: Declaration[] = [];
  for (const year of allYears) {
    const propertiesForYear = properties.filter((p) =>
      (p.compta_fiscal_years || []).includes(year),
    );
    const foldersForYear = propertyFolders.filter((f) =>
      (f.compta_fiscal_years || []).includes(year),
    );

    const grouped: Record<
      number,
      { properties: FiscaliteItem[]; folders: FiscaliteItem[] }
    > = {};
    for (const property of propertiesForYear) {
      const profileId = property.id_profile_nom_owner;
      if (profileId == null) continue;
      if (!grouped[profileId]) {
        grouped[profileId] = { properties: [], folders: [] };
      }
      grouped[profileId].properties.push(property);
    }
    for (const folder of foldersForYear) {
      const profileId = folder.id_profile_nom_owner;
      if (profileId == null) continue;
      if (!grouped[profileId]) {
        grouped[profileId] = { properties: [], folders: [] };
      }
      grouped[profileId].folders.push(folder);
    }

    for (const [profileIdStr, items] of Object.entries(grouped)) {
      const profileId = Number(profileIdStr);
      const profile = profileBailleursById[profileId];
      if (!profile) continue;

      const allItems = [...items.properties, ...items.folders];
      const profileSent = !!profile.is_sent_to_compta;
      const propertiesSent =
        allItems.length > 0 &&
        allItems.every((item) => !!item.is_sent_to_compta);
      const transacSent =
        allItems.length > 0 &&
        allItems.every((item) => !!item.is_transac_sent_to_compta?.[year]);

      const tasksDone =
        Number(profileSent) + Number(propertiesSent) + Number(transacSent);

      declarations.push({
        profileId,
        profileName: profile.nom_profile,
        year,
        properties: items.properties,
        folders: items.folders,
        profile,
        profileSent,
        propertiesSent,
        transacSent,
        tasksDone,
        tasksTotal: 3,
      });
    }
  }

  declarations.sort((a, b) => {
    if (a.year !== b.year) return b.year - a.year;
    return a.profileName.localeCompare(b.profileName);
  });

  return declarations;
}
